import type { PosterDoc, PosterImageSource, TeacherPosterItem } from "@studio/shared";
import type { PdfPaper } from "./exportPdf";
import type { ImageKey } from "./render";

/**
 * 교사 「반 전체 PDF」 묶음 — 학생 한 명씩 그림을 받아 그 페이지만 넘긴다(2026-10-04 후속 2).
 *
 * 🔑 옛 구조는 25명 그림을 전부 ImageBitmap 으로 들고 나서 PDF 를 만들었다. 인쇄용 사본(4K)이면
 *    한 장에 ≈64MB 라 반 하나가 수 GB 가 되어 탭이 죽는다 → 그래서 묶음은 2K 원본만 썼다.
 *    지금은 «받기 → 그리기 → 내려놓기» 를 한 명씩 반복하므로 한 번에 한 학생 그림만 메모리에 있다.
 * 🔑 인쇄용 사본은 A2 · 인쇄 확정한 포스터 · AI 그림(칸)만 받는다. 학생 A2 PDF 와 같은 규칙이고,
 *    확정 안 한 포스터까지 사본을 달라고 하면 서버가 업스케일을 새로 시작해 하루 상한을 쓴다.
 */

export interface BundleMedia {
  blob: Blob | null;
  /** 사본을 달라고 했는데 아직 없어서 원본이 왔다 */
  printPending: boolean;
}

export interface BundleDeps {
  fetchMedia: (workId: string, assetId: string, print: boolean) => Promise<BundleMedia>;
  preload: (doc: PosterDoc, load: (source: PosterImageSource) => Promise<Blob | null>) => Promise<Map<ImageKey, ImageBitmap>>;
  starterDoc: (item: TeacherPosterItem) => PosterDoc;
  onProgress?: (done: number, total: number) => void;
}

export interface BundlePage {
  doc: PosterDoc;
  qrUrl: string | null;
  images: Map<ImageKey, ImageBitmap>;
  /** 이 페이지에서 사본 대신 원본을 쓴 그림 수 */
  printPending: number;
}

export function usesPrintCopy(paper: PdfPaper, item: Pick<TeacherPosterItem, "status">, source: PosterImageSource): boolean {
  return paper === "A2" && item.status === "confirmed" && source.kind === "slot";
}

/** 묶음에 넣을 학생 — 문서가 있거나 그림이 하나라도 있으면 */
export function printableStudents(items: TeacherPosterItem[]): TeacherPosterItem[] {
  return items.filter((item) => !item.is_teacher && (item.doc || item.cuts.some((cut) => cut.image_url)));
}

/** 한 명씩 차례로 — 다음 학생 그림은 앞 페이지를 다 쓴 뒤에야 받는다. */
export async function* bundlePages(items: TeacherPosterItem[], paper: PdfPaper, deps: BundleDeps): AsyncGenerator<BundlePage> {
  for (const [index, item] of items.entries()) {
    deps.onProgress?.(index + 1, items.length);
    const doc = item.doc ?? deps.starterDoc(item);
    let printPending = 0;
    const images = await deps.preload(doc, async (source) => {
      const assetId = source.kind === "slot"
        ? item.cuts.find((cut) => cut.slot === source.slot)?.image_asset_id ?? null
        : source.asset_id;
      if (!assetId) return null;
      const media = await deps.fetchMedia(item.work_id, assetId, usesPrintCopy(paper, item, source));
      if (media.printPending) printPending += 1;
      return media.blob;
    });
    yield { doc, qrUrl: item.ar_url, images, printPending };
  }
}
